/**
 * API 클라이언트
 * ---------------------------------------------------------------------------
 * 토큰 취급 방침 (docs/01-security.md §3.5)
 *   - Access Token 은 메모리에만 둔다. localStorage 에 저장하지 않는다(XSS 노출).
 *   - Refresh Token 은 HttpOnly 쿠키로 서버가 관리한다. JS 에서 접근하지 않는다.
 *   - 401 이 오면 한 번만 갱신을 시도하고, 실패하면 로그인 화면으로 보낸다.
 */

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getSupportSession, setSupportSession, type SupportSession } from './support-mode';

// 기본값은 상대 경로다. vite 프록시를 거쳐 동일 출처가 되므로
// CORS 와 SameSite=strict 쿠키 문제가 생기지 않는다.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * 토큰 갱신 재시도를 적용하지 않는 경로.
 *
 * 로그인 실패(401)에 갱신 재시도를 걸면, 갱신도 실패해 로그인 화면으로
 * 강제 이동(새로고침)하면서 **"비밀번호가 틀렸다"는 오류 메시지가 지워진다.**
 * 사용자 눈에는 아무 일도 일어나지 않은 것처럼 보인다.
 */
const NO_RETRY_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/mfa/verify',
  '/auth/mfa/resend',
  '/auth/invite/accept',
  '/support/refresh',
];

/** 메모리에만 보관한다. 새로고침하면 사라지고 refresh 쿠키로 복구한다. */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  withCredentials: true, // refresh 쿠키 전송
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const url = original?.url ?? '';

    const skipRetry = NO_RETRY_PATHS.some((path) => url.startsWith(path));

    if (error.response?.status !== 401 || original?._retried || skipRetry) {
      return Promise.reject(normalizeError(error));
    }

    original._retried = true;

    // 동시 요청이 여러 개 401 을 받아도 갱신은 한 번만 수행한다.
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });

    const newToken = await refreshPromise;
    if (!newToken) {
      redirectToLogin();
      return Promise.reject(normalizeError(error));
    }

    original.headers.set('Authorization', `Bearer ${newToken}`);
    return apiClient(original);
  },
);

async function refreshAccessToken(): Promise<string | null> {
  // 지원 모드 중에는 반드시 지원 세션으로 갱신한다.
  // /auth/refresh 로 갱신하면 토큰이 운영자의 소속 기업으로 바뀌는데,
  // 화면은 그대로여서 고객사 데이터를 보고 있다고 착각하게 된다.
  if (getSupportSession()) {
    const renewed = await renewSupportSession();
    // 지원 세션이 만료됐다면 운영자 본인 세션은 아직 살아 있다.
    // 로그인 화면으로 내쫓지 말고 자기 콘솔로 되돌린다.
    if (renewed) return renewed;
  }

  try {
    const { data } = await axios.post<{ accessToken: string }>(
      `${API_BASE}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    setAccessToken(data.accessToken);
    return data.accessToken;
  } catch {
    setAccessToken(null);
    return null;
  }
}

/**
 * 지원 세션 Access Token 갱신.
 *
 * 실패하면(만료·종료) 지원 모드 상태를 비우고 null 을 돌려준다.
 * 호출자는 일반 세션으로 되돌리거나 로그인 화면으로 보낸다.
 */
export async function renewSupportSession(): Promise<string | null> {
  try {
    const { data } = await axios.post<SupportSession & { accessToken: string }>(
      `${API_BASE}/support/refresh`,
      {},
      { withCredentials: true },
    );
    setAccessToken(data.accessToken);
    setSupportSession({
      supportSessionId: data.supportSessionId,
      tenantId: data.tenantId,
      companyName: data.companyName,
      expiresAt: data.expiresAt,
    });
    return data.accessToken;
  } catch {
    setSupportSession(null);
    setAccessToken(null);
    return null;
  }
}

/**
 * 로그인 화면으로 돌려보낸다.
 *
 * `reason=expired` 를 붙여 **왜 쫓겨났는지** 알려 준다. 세션은 30분 유휴로
 * 끊기는데, 아무 말 없이 로그인 화면만 뜨면 비밀번호가 틀린 줄 알고 다시
 * 입력해 보게 된다.
 */
function redirectToLogin(): void {
  if (window.location.pathname !== '/login') {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}&reason=expired`);
  }
}

export interface NormalizedError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
  requestId?: string;
  status?: number;
  /**
   * 거절과 함께 온 판단 근거.
   *
   * 「계약 인원을 넘습니다」처럼 **무엇을 물어봐야 할지**가 응답에 담겨 오는
   * 경우가 있다. 금액을 모르면 화면이 「예/아니오」를 물을 수 없으므로
   * 원문을 그대로 들고 간다. 쓰는 쪽에서 자기가 아는 모양으로 읽는다.
   */
  payload?: Record<string, unknown>;
}

/**
 * 서버 오류를 화면에 보여줄 형태로 정규화한다.
 * 원인을 구분해 주므로(서버 미기동 vs 미구독 vs 권한없음) 안내 문구를 다르게 띄울 수 있다.
 */
function normalizeError(error: AxiosError): NormalizedError {
  // 응답 자체가 없으면 네트워크 문제다. 개발 중 가장 흔한 원인은
  // API 서버가 떠 있지 않은 것이므로, 추측하지 말고 그대로 알려 준다.
  if (!error.response) {
    const target = API_BASE.startsWith('http') ? API_BASE : `${window.location.origin}${API_BASE}`;
    const message =
      error.code === 'ECONNABORTED'
        ? '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.'
        : `API 서버에 연결할 수 없습니다 (${target}). ` +
          'API 서버가 실행 중인지 확인해 주세요.';
    return { code: 'NETWORK_ERROR', message, status: 0 };
  }

  const body = error.response.data as Partial<NormalizedError> | undefined;

  // 개발 중 vite 프록시가 API 에 닿지 못하면 502 + API_UNREACHABLE 로 내려온다.
  // 그대로 보여주어야 "API 를 안 켰다"는 사실이 화면에서 드러난다.
  if (body?.code === 'API_UNREACHABLE') {
    return {
      code: body.code,
      message: body.message ?? 'API 서버에 연결할 수 없습니다.',
      status: error.response.status,
    };
  }

  if (body?.code === 'ENTITLEMENT_REQUIRED') {
    return {
      code: body.code,
      message: '현재 요금제에 포함되지 않은 기능입니다. 관리자에게 문의하세요.',
      status: error.response.status,
      requestId: body.requestId,
    };
  }

  return {
    code: body?.code ?? 'UNKNOWN',
    message: body?.message ?? '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
    details: body?.details,
    requestId: body?.requestId ?? (error.response.headers?.['x-request-id'] as string | undefined),
    status: error.response.status,
    payload: (body ?? undefined) as Record<string, unknown> | undefined,
  };
}
